/**
 * ㉗ 설계안 CAFE (2026-10-06 11:50, 세션 A · docs/cafe/design_cafe_20261006.md md5 95def524…) — 레인들이 같이 쓰는 «계약».
 *
 * 이 파일이 정하는 것: 메뉴 키·지역↔메뉴 지도 · 큐 행/초안 타입 · 앵커·UTM 마크업 · 본문 이미지 마커 · 자체 이미지 생성기 URL 규약 ·
 *   app_config naver_cafe.* 키 · 주(ISO) 키. 레인은 «여기 없는 것» 만 자기 파일에 둔다. 이 파일을 바꿔야 하면 바꾸지 말고 보고한다.
 *
 * 흐름: 생성기(C1~C9, src/lib/cafe/generators/*) → CafeDraft → cafe_queue(ref_key 로 중복 방지) → 게시기(naver-cafe-publish)가
 *   품질 관문 → 이미지 받기·JPG → multipart(본문 + image[0..n]) 전송.
 *   draft.html 의 이미지 자리는 `<img src='#0'>`… 마커(+ 바로 뒤 캡션 <p>), images 배열의 «순서» 가 번호다.
 *   draft.html 의 링크는 항상 앵커 마크업 `<a href='URL'>앵커 문구</a>` 로 «저장» 한다 — 게시 시점의 naver_cafe.anchor_mode 가
 *   text(기본: 「앵커 문구 (URL)」로 풀어 씀) / anchor(<a> 보존) 를 정한다. ⛔ 맨 URL 글자를 html 에 직접 쓰지 않는다.
 */
import { kstNow } from '@/lib/cl-core/kst';
import { escText } from '@/lib/html/esc';
import { CAFE_UTM_ALLOWED_CONTENT } from '@/lib/cafe/utm-policy';
import { SIDO_17, SIDO_17_CAPITAL_FIRST } from '@/lib/cl-core/names';
import { CANONICAL_ORIGIN } from '@/lib/cl-core/config';

export const CAFE_CLUB_ID = '31708817';
export const CAFE_BASE_URL = 'https://cafe.naver.com/kadeoraapp';
export const CAFE_SITE_URL = CANONICAL_ORIGIN; // C1 — 정본 cl-core/config(값 동일)

// ── 메뉴 ────────────────────────────────────────────────────────────────────
/** 17개 시·도(활성 현장 수 순서가 아니라 행정 순서). apt_sites.region 의 표기와 같다. */
//   B4: 값은 cl-core/names SIDO_17_CAPITAL_FIRST 와 같다(특성 테스트 b4-sido-single-source 가 잠근다). 타입(리터럴 튜플)이 필요해 SIDO_17 에서 만든다.
export type Sido = (typeof SIDO_17)[number];
export const SIDO_LIST = SIDO_17_CAPITAL_FIRST as readonly Sido[];

export const REGION_MENU_KEY: Record<Sido, string> = {
  서울: 'r_seoul', 경기: 'r_gyeonggi', 인천: 'r_incheon', 부산: 'r_busan', 대구: 'r_daegu', 광주: 'r_gwangju', 대전: 'r_daejeon', 울산: 'r_ulsan', 세종: 'r_sejong',
  강원: 'r_gangwon', 충북: 'r_chungbuk', 충남: 'r_chungnam', 전북: 'r_jeonbuk', 전남: 'r_jeonnam', 경북: 'r_gyeongbuk', 경남: 'r_gyeongnam', 제주: 'r_jeju',
};
/**
 * v4(2026-10-07 · docs/cafe/design_cafe_20261007_v4_menus.md) — 주제 게시판 키 19개. 지역 키 r_* 는 «자동 글에서 폐기»(REGION_MENU_KEY 는 웹앱→카페 링크 호환용으로만 남는다).
 * 모든 생성기는 이 키만 쓴다. 지역은 제목 첫 낱말 + ref.region 으로 표현한다.
 */
export const TOPIC_MENU_KEYS = [
  'notice', 'glossary', 'sub_schedule', 'sub_price', 'sub_result', 'unsold', 'redev', 'recon', 'trade', 'trade_high',
  'rent', 'site', 'movein', 'region', 'free', 'qna', 'local', 'hello', 'ops',
  // next-44 — 세션 A 가 개통한 새 게시판 5개(menu_names: 뉴스=30 · 브리핑=31 · 자금=32 · 운영자 현장=33 · TOP=34)
  'news', 'briefing', 'finance', 'top',
  // cafe-boost ⑤ — 멤버 자료실(37 · 멤버 공개 글만)
  'members',
] as const;
export type TopicMenuKey = (typeof TOPIC_MENU_KEYS)[number];
export type CafeMenuKey = TopicMenuKey | (typeof REGION_MENU_KEY)[Sido];

export const TOPIC_MENU_LABEL: Record<TopicMenuKey, string> = {
  notice: '공지·이용안내', glossary: '용어·데이터 출처', sub_schedule: '청약 일정', sub_price: '분양가·평형 분석', sub_result: '청약 경쟁률·당첨',
  unsold: '미분양·무순위 줍줍', redev: '재개발 단계 소식', recon: '재건축·리모델링', trade: '실거래 주간 리포트', trade_high: '신고가·급등락',
  rent: '전월세 실거래', site: '분양 현장 분석', movein: '입주 예정·입주장', region: '지역별 주간 리포트', free: '자유게시판', qna: '질문답변',
  local: '우리 동네 이야기', hello: '가입인사', ops: '운영 점검',
  news: '부동산 뉴스·정책', briefing: '오늘의 부동산 브리핑', finance: '자금계획·세금·대출', top: '어제의 TOP글', members: '멤버 자료실',
};
/** 1차 개통 5개(설계안 §0-B ②) — 나머지 12개는 메뉴만 만들어 두고 글이 10편 쌓이면 공개. */
export const PHASE1_SIDO: readonly Sido[] = ['경기', '서울', '부산', '경남', '인천'];
/** 시·도 이름(또는 「부산광역시」 꼴) → 메뉴 키. 모르면 null(예: 통합 표기 「전남광주」). */
export function regionMenuKey(region: string | null | undefined): string | null {
  const s = String(region ?? '').trim().replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '');
  return (REGION_MENU_KEY as Record<string, string>)[s] ?? null;
}

/** 카페 메뉴 목록 URL(앱→카페 링크용). ⚠️ 형식 미검증 — 클래식 ArticleList 주소. */
export function cafeMenuUrl(menuId: number | string): string {
  return `https://cafe.naver.com/ArticleList.nhn?search.clubid=${CAFE_CLUB_ID}&search.menuid=${encodeURIComponent(String(menuId))}`;
}

// ── app_config naver_cafe.* ────────────────────────────────────────────────
/** 키 이름 한 곳. 값 모양은 주석. 쓰기는 세션 A(스위치) / 게시기(카운터·999·halved 만). */
export const CAFE_CFG = {
  enabled: 'enabled',                 // boolean — 세션 A 만
  menus: 'menus',                     // Record<menu_key, number> — SP 가 수집, 세션 A 가 넣는다. 없는 키의 글은 «보류»
  menuPublic: 'menu_public',          // string[] — 공개된 메뉴 키(웹앱→카페 링크는 여기 있을 때만)
  anchorMode: 'anchor_mode',          // 'text'(기본) | 'anchor'
  dailyCap: 'daily_cap',              // number, 기본 8(→12→20). 4주 판정 전 20 초과 금지
  dailyCounter: 'daily_counter',      // {date,count} KST (기존)
  consecutiveFailures: 'consecutive_failures', // 기존 — 3 이상이면 정지
  err999: 'err999',                   // {date,count} KST — 하루 999 횟수(설계안 §0-B ⑥)
  capHalvedDate: 'cap_halved_date',   // 'YYYY-MM-DD' KST — 이 날은 상한 절반
} as const;
export type CafeAnchorMode = 'text' | 'anchor';

// ── 큐 ──────────────────────────────────────────────────────────────────────
export type CafeKind = 'C1' | 'C2' | 'C2b' | 'C3' | 'C4' | 'C4m' | 'C5' | 'C6' | 'C7' | 'C7b' | 'C8' | 'C9' | 'C10' | 'C11' | 'N' | 'S' | 'SEED' | 'P1' | 'P1c' | 'P2' | 'P2w' | 'P3' | 'P5' | 'P6' | 'B1' | 'N1' | 'F1' | 'L1' | 'L2' | 'L3' | 'PR' | 'P4' | 'P7' | 'RT' | 'TH' | 'HOT' | 'CX' | 'RD' | 'UM' | 'MM' | 'PE' | 'PW' | 'ND' | 'IS' | 'RB' | 'TP' | 'MS';   // cafe-boost ④⑤ — ND·IS·RB·TP·MS

/** cafe_queue.kind CHECK 와 «같은» 목록(supabase/migrations/20261008_cafe_queue_kind_check.sql). 디스패처·생성기가 내는 kind 는 전부 여기에 있어야 INSERT 가 통과한다. C9w 는 CHECK 에만 있는 예비 값(현재 청약 일정판은 kind C9). */
export const CAFE_KINDS_ALL: readonly string[] = ['C1', 'C2', 'C2b', 'C3', 'C4', 'C4m', 'C5', 'C6', 'C7', 'C7b', 'C8', 'C9', 'C9w', 'C10', 'C11', 'N', 'S', 'SEED', 'P1', 'P1c', 'P2', 'P2w', 'P3', 'P5', 'P6', 'B1', 'N1', 'F1', 'L1', 'L2', 'L3', 'PR', 'P4', 'P7', 'RT', 'TH', 'HOT', 'CX', 'RD', 'UM', 'MM', 'PE', 'PW', 'ND', 'IS', 'RB', 'TP', 'MS'];   // cafe-boost — docs/cafe/pg_cron_boost.sql 이 CHECK 에 더한다

/** v4 — 우선순위(작을수록 먼저). C8 은 전국 20(cafePriority · 레인 K — 종전 부산 20·그 밖 30). */
export const CAFE_PRIORITY: Readonly<Record<string, number>> = { N: 0, C9: 1, C2: 2, P3: 2, F1: 6, C3: 3, C7: 4, C5: 5, C1: 6, C4: 7, C4m: 7, P1: 6, P1c: 7, P2: 5, P2w: 5, P5: 8, P6: 8, C6: 8, C2b: 9, C7b: 10, C10: 11, C11: 12, S: 15, L1: 7, L2: 6, L3: 4, P4: 2, P7: 5, PR: 9, RT: 9, TH: 2, HOT: 5, CX: 9, RD: 9, UM: 5, MM: 9, PE: 6, PW: 9, TP: 3, MS: 4, ND: 5, IS: 5, RB: 6 };   // cafe-boost — TP·MS 일 1·주 1 · 소식·이슈·구별 브리핑   // nk_01 §7-R5 — P4 = P3급 속보 · P7 중간 · PR 평시
export function cafePriority(kind: string, region?: string | null): number {
  // 레인 K (2026-10-26) — C8 지역 차등(부산 20 · 그 밖 30) 폐지: 전국 20.
  if (kind === 'C8') { void region; return 20; }
  return CAFE_PRIORITY[kind] ?? 50;
}
export type CafeQueueStatus = 'queued' | 'held' | 'sending' | 'posted' | 'failed' | 'skipped';

export interface CafeDraftImage {
  /** 첨부 순서가 곧 본문 `<img src='#n'>` 의 n. url 은 자체 호스트만(kadeora.app·*.supabase.co) — 외부는 게시기가 거른다. */
  url: string;
  caption: string;
  /** 정보용 — 'site_card' | 'region_map' | 'cafe-img:trade-top5' … */
  kind?: string;
}
export interface CafeDraftAnchor { text: string; url: string }

export interface CafeDraft {
  kind: CafeKind;
  menu_key: string;
  /** 같은 초안을 두 번 만들지 않는 키 — 예: 'C1:부산:2026-W41' · 'C2:<site slug>:d7' · 'C8:syn:189'. cafe_queue.ref_key UNIQUE. */
  ref_key: string;
  /** 참조(현장 slug·지역·글 id·주 키 등). 같은 현장 7일 중복 판정은 ref.site_slug 로 한다. */
  ref: { site_slug?: string; region?: string; sigungu?: string; blog_post_id?: number; syndication_id?: number; week?: string; [k: string]: unknown };
  subject: string;
  /** null 이면 게시 시점에 만든다(C8: blog_posts 원문 → v2 본문). 그 외 종류는 반드시 채운다. */
  html: string | null;
  images: CafeDraftImage[];
  anchors: CafeDraftAnchor[];
  priority: number;
  not_before: string | null;
}
export interface CafeQueueRow extends CafeDraft {
  id: number;
  status: CafeQueueStatus;
  article_id: string | null;
  error: string | null;
  attempts: number;
  created_at: string;
  posted_at: string | null;
}

export interface GenCtx {
  /** service-role supabase client(any). 생성기는 SELECT 만 한다 — 쓰기는 라우트가 cafe_queue 에만. */
  sb: any;
  now: Date;
  siteUrl: string;
  /** 시험·부분 실행용 제한(지역명·현장 slug 등). 생성기가 알아서 해석하고 모르면 무시. */
  only?: string;
  limit?: number;
  /** v4 — 개통 시드용 1회 실행: 각 유형의 창을 넓힌다(평시는 짧다). */
  backfill?: boolean;
  /** v4 02c — C1 지역 목록(`regions=all` 이면 undefined · `regions=부산,서울`). */
  regions?: string[];
  /** next-44 — 이미 큐에 있는 것으로 치고 건너뛸 ref_key(드라이 시뮬레이션용). */
  exclude?: ReadonlySet<string>;
  /** v4 02c — 생성기가 «건너뛴 이유» 를 세어 디스패처 응답에 싣는다. */
  skip?: (reason: string, n?: number) => void;
  /** v4 02c — 생성기가 응답에 덧붙일 자유 보고(예: S 의 파일별 편수). */
  report?: Record<string, unknown>;
  /** KWX 레인 A — 게시판별 남은 몫(생성 라우트 board-headroom · 읽기 전용). 앞줄(due 현장)을 «오늘 나갈 자리가 있는 게시판» 만큼만 세워 몫을 헛쓰지 않게. 없으면 거르지 않음. */
  boardRoom?: (menuKey: string) => number;
}
export interface CafeGenerator {
  kind: CafeKind;
  generate(ctx: GenCtx): Promise<CafeDraft[]>;
}

// ── 앵커·링크·이미지 마커 (seo1 C3: 본문 링크 utm 0 · 글당 링크 상한 = cafe/link-cap CAFE_LINK_MAX) ────────────────────────────────────────────────────
/** 한글 경로는 «디코딩 상태» 가 아니라 href 에서는 인코딩해 둔다(<a> 보존 모드에서 그대로 클릭돼야 한다). */
function encPath(path: string): string {
  const [p, q] = String(path).split('?');
  const enc = p.split('/').map((seg) => { try { return encodeURIComponent(decodeURIComponent(seg)); } catch { return encodeURIComponent(seg); } }).join('/');
  return q ? `${enc}?${q}` : enc;
}
/** 카더라 URL(카페 본문 링크). path 는 '/apt/…' 꼴.
 *  seo1 C3 — 카페 본문의 kadeora.app 링크에는 utm 을 붙이지 않는다(정본 URL 그대로 — 검색·AI 브리핑이 utm 꼬리 URL 을 따로 보지 않게).
 *  유입 구분은 리퍼러(cafe.naver.com)로 충분하다(leads.utm·apt_marketing_leads 60일 실측: naver_cafe/community 0건).
 *  seo1d — /go/* 측정 리다이렉트도 utm 0(정책 단일 출처 cafe/utm-policy). 예외는 배너 A/B 의 utm_content=head|head_b 하나뿐.
 *  이름·kind 인자는 호환용(쓰지 않는다). */
export function cafeUtmUrl(path: string, _kind: CafeKind | string, siteUrl: string = CAFE_SITE_URL, content?: string): string {
  const base = siteUrl.replace(/\/+$/, '') + encPath(path.startsWith('/') ? path : `/${path}`);
  if (!/^\/*go\//.test(path) || !content || !(CAFE_UTM_ALLOWED_CONTENT as readonly string[]).includes(content)) return base;
  return `${base}${base.includes('?') ? '&' : '?'}utm_content=${content}`;
}
/** 앵커 마크업(저장 형식). href 는 작은따옴표(곧은 큰따옴표는 999). `&` 는 &amp;. */
export function cafeAnchorTag(text: string, url: string): string {
  const href = url.replace(/&(?!amp;)/g, '&amp;').replace(/'/g, '%27');
  return `<a href='${href}'>${escText(text).replace(/["“”]/g, '')}</a>`;
}
/** 앵커 1개 = [마크업, 메타]. 호출부는 마크업을 html 에 끼우고 메타를 draft.anchors 에 넣는다. */
export function cafeAnchor(text: string, path: string, kind: CafeKind | string, siteUrl: string = CAFE_SITE_URL, content?: string): { tag: string; meta: CafeDraftAnchor } {
  const url = cafeUtmUrl(path, kind, siteUrl, content);
  return { tag: cafeAnchorTag(text, url), meta: { text, url } };
}
/** 본문 이미지 마커 + 캡션 1줄(`<p><img src='#n'></p><p>캡션</p>`). 캡션의 곧은 큰따옴표는 제거. */
export function cafeImgTag(n: number, caption: string): string {
  const cap = escText(caption).replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();
  return `<p><img src='#${n}'></p>${cap ? `<p>${cap}</p>` : ''}`;
}

// ── 자체 이미지 생성기(1200×630 JPG/PNG, 글씨 ≥40px) URL 규약 ───────────────────
/**
 * 구현은 src/app/api/cafe-img/[kind]/route.tsx (레인 I). 생성기 레인은 «URL 만» 만든다.
 *   trade-top5    {region, sigungu?, start, end, ev?}  실거래 상위 5 표 — 기간 start~end(양끝 포함, 본문과 «같은 기간»·같은 쿼리·같은 정렬: src/lib/cafe/trade-period.ts).
 *                                                       ev = 사건 한 줄(예: 「미사강변리버뷰자이 15억 3,000만 신고가 +22.9%」) — 있으면 맨 위 큰 글씨 1줄로 그린다. (asof 는 옛 호환)
 *   trade-trend   {region, sigungu?, weeks?=12, end?}    가격 추이 꺾은선 — end 가 있으면 그 날이 든 주까지
 *   unsold-bars   {month?=YYYY-MM}               시·도 미분양 막대
 *   sub-timeline  {site? | region?+(start,end | week)}   청약 일정 타임라인 — 기간 start~end(없으면 week). 데이터가 0이면 404(초안에서 빼야 한다)
 *   price-table   {site}                         평형별 분양가 표
 *   comp-bars     {site}                         경쟁률 막대
 *   redev-progress{site}                         재개발 단계 진행 막대
 *   rent-ratio-trend {region?, weeks?=12}        전세가율 꺾은선(최근 12주) — 데이터가 모자라면 404
 *   kakao-banner  {v=0|1|2}                       카톡방 입장 가로 배너(1200×360) — 문안 3종 로테이션, 데이터 없음(항상 그려짐). NC Phase 3
 *   board-card    {menu=<menu_key>}              게시판 대표 카드(큰 글씨 제목 카드) — 시드·안내 글의 첫 장. 데이터 없음(항상 그려짐), 모르는 키면 404
 *   heatmap       {metric=trade|unsold|redev, start?, end?} 시·도 히트맵 지도 — trade 는 기간 start~end(asof 는 옛 호환)
 *   summary90     {region, sigungu?, apt?, name?, tbl?=presale, end?}  nc_14b — 최근 90일 거래 건수·중위·최고·최저 4지표 카드(표 대체). 건수 <3 이거나 조회가 1,000행을 넘으면 404
 *   trend         {region, sigungu?, apt?, name?, tbl?, end?, weeks?=12}  nc_14b — 주별 중위 거래가 꺾은선(주 ≥2건 · 점 ≥4 · 마지막 점이 최근 2주 안, 아니면 404)
 *   timeline      {site}                         nc_14b — 공고→접수→발표→계약 4단계(날짜 없는 단계는 「미정」, 날짜 있는 단계 <2 이면 404)
 *   stat-card     {t, r=「라벨~값|…」(2~4), s?}   cafe-boost — 생성기가 본문에 실은 값 2~4개를 지표 타일로(DB 0 · 행 <2 이면 404). 이미지 최소 장수 미달 킨드용
 *   rent90        {region, sigungu?, apt?, name?, end?}  nm_03 — 최근 90일 전월세 건수·전세 중위 보증금·월세 중위(보증금/월세) 4지표 카드. 건수 <5 이거나 조회가 600행에 닿으면 404
 */
export const CAFE_IMG_GEN_KINDS = ['trade-top5', 'trade-trend', 'unsold-bars', 'sub-timeline', 'price-table', 'comp-bars', 'redev-progress', 'heatmap', 'rent-ratio-trend', 'board-card', 'kakao-banner', 'summary90', 'trend', 'timeline', 'rent90', 'stat-card'] as const;
export type CafeImgGenKind = (typeof CAFE_IMG_GEN_KINDS)[number];
export function cafeImgUrl(kind: CafeImgGenKind, params: Record<string, string | number | null | undefined> = {}, siteUrl: string = CAFE_SITE_URL): string {
  const q = Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
  return `${siteUrl.replace(/\/+$/, '')}/api/cafe-img/${kind}${q ? `?${q}` : ''}`;
}

// ── 주 키 ──────────────────────────────────────────────────────────────────
/** KST 기준 ISO 주 키 'YYYY-Www'. */
export function isoWeekKey(now: Date = new Date()): string {
  const k = kstNow(now.getTime());
  const d = new Date(Date.UTC(k.getUTCFullYear(), k.getUTCMonth(), k.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y = d.getUTCFullYear();
  const wk = Math.ceil(((d.getTime() - Date.UTC(y, 0, 1)) / 86400000 + 1) / 7);
  return `${y}-W${String(wk).padStart(2, '0')}`;
}
